import Chart from "chart.js/auto";
import { CONTENT_WIDTH, bottomMarginItemGlobal } from "../../utils/pdfConstants";

// Entire block is composited onto ONE canvas and handed to pdfmake as a
// single image, rather than assembled from several pdfmake primitives
// (stat table, two chart images, a year table, a summary table). That
// multi-primitive approach kept overflowing the page because pdfmake had
// to lay out 11+ table columns and multiple blocks together; a single
// pre-sized image removes that layout step entirely — pdfmake just places
// one image at `width: contentWidth`. Not a DOM screenshot (no
// html2canvas) — every element here is drawn/composited programmatically,
// same as the Chart.js-canvas-to-PNG technique this file already used,
// just assembled onto one shared canvas instead of several separate ones.

const GREEN = "#198754";
const MUTED = "#8e8e93";
const BORDER = "#e5e7eb";

const CATEGORIES = [
  { key: "tekniikkaYhteensa", name: "Rakennetekniikka", color: "#14532d" },
  { key: "lviYhteensa", name: "LVI Järjestelmät", color: "#2f8f4e" },
  { key: "sahkoYhteensa", name: "Sähköjärjestelmät", color: "#7cbf7f" },
  { key: "tutkimusYhteensa", name: "Lisätutkimukset", color: "#bfe0a8" },
];

// Master canvas is sized ≈ CONTENT_WIDTH (538.58pt) × 2, matching the
// scale:2 convention PTSLongTermTable.js's own html2canvas capture
// already uses elsewhere in this same PTS feature for image crispness.
const IMG_WIDTH = 1080;
const PAD = 24;
const USABLE_W = IMG_WIDTH - PAD * 2;

function getArea(rakennusData, mergedRakennusData, field) {
  const dataToUse = mergedRakennusData?.rakennukset_fulls?.length > 0 ? mergedRakennusData : rakennusData || {};
  const buildings = Array.isArray(dataToUse.rakennukset_fulls) ? dataToUse.rakennukset_fulls : [];
  const total = buildings
    .filter(r => r.isMainBuilding)
    .reduce((sum, r) => {
      const val = Number(String(r[field] || "").replace(/\s/g, "").replace(",", "."));
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);
  return total || 1;
}

const sumThousands = (arr = []) => arr.reduce((a, b) => a + (parseFloat(b) || 0), 0) * 1000;
const fmtEur = v => `${Math.round(v).toLocaleString("fi-FI")} €`;
const fmtM2Month = v => `${v.toFixed(2).replace(".", ",")} €/m²/kk`;

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, rr);
  } else {
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }
}

// Shared style for every "SECTION TITLE" line (Capex-yhteenveto, Jakautuminen,
// Kustannusarvio vuosittain, Toimenpide-ehdotusten yhteenveto), matching the
// screen version's single ".capex-section-title" CSS class. Returns the
// height consumed (text + gap before the next element).
function drawSectionTitle(ctx, x, y, text) {
  ctx.textAlign = "left";
  ctx.fillStyle = GREEN;
  ctx.font = "bold 14px Lato, sans-serif";
  ctx.fillText(text.toUpperCase(), x, y + 12);
  return 28;
}

// Per-year, matching the reference's own "cost estimate per year" chart
// (one bar per year, full 4-digit year on the axis) — the 0–1/2–5/6–10
// bucketing is for the toimenpide-ehdotukset summary only, not this chart.
// No in-chart title: the section title is drawn separately above it
// (drawSectionTitle), matching the screen version's layout. Canvas is
// created at its final on-image size directly, so it can be drawImage'd
// onto the master canvas at 1:1 scale with no extra aspect-ratio math.
function renderYearChart(years, yearTotals, w, h) {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  new Chart(ctx, {
    type: "bar",
    data: {
      labels: years.map(y => String(y)),
      datasets: [{ data: yearTotals.map(v => Math.round(v)), backgroundColor: GREEN, borderRadius: 4, maxBarThickness: 28 }],
    },
    options: {
      // Without this, Chart.js scales its internal pixel buffer by
      // window.devicePixelRatio even with responsive:false — on a HiDPI
      // screen the canvas ends up wider than the `w` we created it at, so
      // the later ctx.drawImage(yearCanvas, x, y) (no dest width/height)
      // draws it oversized and it gets clipped by the master canvas's
      // right edge. Pinning it to 1 keeps the canvas's actual pixel size
      // exactly (w, h), matching what drawImage expects.
      devicePixelRatio: 1,
      animation: false,
      responsive: false,
      layout: { padding: { top: 8 } },
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: { beginAtZero: true, grid: { color: "#eeeeee" }, ticks: { font: { size: 11 } } },
      },
    },
  });
  // No chart.destroy() here — it clears the canvas as part of teardown,
  // and this canvas still needs to be drawImage'd onto the master canvas
  // by the caller. (The other PDF chart renderers in this app destroy
  // right after their own toDataURL() capture, which is safe because
  // nothing reads the canvas afterward — this one is read later instead.)
  return canvas;
}

// Category breakdown is drawn as plain progress bars, not a Chart.js
// chart — matching the screen version (CapexYhteenveto.jsx), which never
// used a chart here either. Rows are spread across `availableH` (the
// right column's height) so the two columns come out equal height
// instead of the category list stopping short and leaving a gap.
function drawCategoryList(ctx, x, y, w, categories, availableH) {
  const grandTotal = categories.reduce((a, c) => a + c.total, 0) || 1;
  let cursorY = y;
  cursorY += drawSectionTitle(ctx, x, cursorY, "Jakautuminen");

  const rowH = Math.max(42, (availableH - 28) / categories.length);
  const barH = 8;
  categories.forEach(c => {
    const pct = (c.total / grandTotal) * 100;

    ctx.textAlign = "left";
    ctx.fillStyle = "#1c1c1e";
    ctx.font = "bold 13px Lato, sans-serif";
    ctx.fillText(c.name, x, cursorY + 13);

    ctx.textAlign = "right";
    ctx.fillStyle = MUTED;
    ctx.font = "13px Lato, sans-serif";
    ctx.fillText(`${fmtEur(c.total)} · ${pct.toFixed(0)} %`, x + w, cursorY + 13);

    const barY = cursorY + 20;
    ctx.fillStyle = "#eeeeee";
    roundRect(ctx, x, barY, w, barH, barH / 2);
    ctx.fill();

    const fillW = Math.max(barH, (w * pct) / 100);
    ctx.fillStyle = c.color;
    roundRect(ctx, x, barY, fillW, barH, barH / 2);
    ctx.fill();

    cursorY += rowH;
  });

  return cursorY - y;
}

function drawStatTile(ctx, x, y, w, h, label, value, sub) {
  ctx.fillStyle = "#fafafa";
  roundRect(ctx, x, y, w, h, 10);
  ctx.fill();

  ctx.textAlign = "left";
  ctx.fillStyle = "#666";
  ctx.font = "bold 13px Lato, sans-serif";
  ctx.fillText(label, x + 18, y + 28);

  ctx.fillStyle = GREEN;
  ctx.font = "bold 26px Lato, sans-serif";
  ctx.fillText(value, x + 18, y + 62);

  ctx.fillStyle = MUTED;
  ctx.font = "15px Lato, sans-serif";
  ctx.fillText(sub, x + 18, y + 86);
}

function drawYearTable(ctx, x, y, w, years, footerTotals, capexTotal) {
  const cols = years.length + 2; // blank label col + years + total col
  const colW = w / cols;
  ctx.textAlign = "center";

  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + 30);
  ctx.lineTo(x + w, y + 30);
  ctx.stroke();

  ctx.fillStyle = "#666";
  ctx.font = "bold 15px Lato, sans-serif";
  years.forEach((yr, idx) => {
    ctx.fillText(String(yr).slice(-2), x + colW * (idx + 1) + colW / 2, y + 20);
  });
  ctx.fillText("Yht.", x + colW * (cols - 1) + colW / 2, y + 20);

  ctx.font = "16px Lato, sans-serif";
  ctx.fillStyle = "#1c1c1e";
  footerTotals.forEach((v, idx) => {
    ctx.fillText(String(Math.round(v || 0)), x + colW * (idx + 1) + colW / 2, y + 48);
  });
  ctx.font = "bold 16px Lato, sans-serif";
  ctx.fillText(String(Math.round(capexTotal / 1000)), x + colW * (cols - 1) + colW / 2, y + 48);

  return 62; // height consumed
}

function drawActionBox(ctx, x, y, w, periods, capexTotal, alvLabel) {
  ctx.textAlign = "left";
  ctx.fillStyle = GREEN;
  ctx.font = "bold 14px Lato, sans-serif";
  ctx.fillText("TOIMENPIDE-EHDOTUSTEN YHTEENVETO", x, y + 14);
  const titleWidth = ctx.measureText("TOIMENPIDE-EHDOTUSTEN YHTEENVETO").width;
  ctx.fillStyle = "#888";
  ctx.font = "13px Lato, sans-serif";
  ctx.fillText(`  (${alvLabel})`, x + titleWidth, y + 14);

  const boxY = y + 24;
  const boxH = 90;
  ctx.fillStyle = "#eafbf1";
  roundRect(ctx, x, boxY, w, boxH, 10);
  ctx.fill();

  const items = [...periods, { label: "Yhteensä", total: capexTotal }];
  const colW = w / items.length;
  ctx.textAlign = "center";
  ctx.strokeStyle = "#bfe0a8";
  ctx.lineWidth = 1;
  items.forEach((item, idx) => {
    const cx = x + colW * idx + colW / 2;
    if (idx > 0) {
      ctx.beginPath();
      ctx.moveTo(x + colW * idx, boxY + 14);
      ctx.lineTo(x + colW * idx, boxY + boxH - 14);
      ctx.stroke();
    }
    ctx.fillStyle = "#666";
    ctx.font = "bold 12px Lato, sans-serif";
    ctx.fillText(item.label.toUpperCase(), cx, boxY + 34);
    ctx.fillStyle = GREEN;
    ctx.font = "bold 18px Lato, sans-serif";
    ctx.fillText(fmtEur(item.total), cx, boxY + 62);
  });

  return 24 + boxH;
}

// Layout mirrors the screen version (CapexYhteenveto.jsx) section for
// section: title, stat tiles, a Jakautuminen/Kustannusarvio two-column
// row, then the action-proposals box — not the earlier two-Chart.js-
// images-side-by-side version, which looked structurally different from
// the screen component it was meant to match. Heights are pre-computed
// so the master canvas is sized exactly, with no leftover whitespace or
// clipping.
function buildCapexImage({ capexTotal, currentYear, perGrossM2Month, grossArea, perNetM2Month, netArea, categories, years, footerTotals, periods, alviton = false }) {
  // Label-only — every figure here is the same as entered in the PTS tables
  // (VAT-inclusive, no VAT math applied); this flag only picks which
  // phrasing is printed next to them.
  const alvLabel = alviton ? "ALV 0 %" : "sis. ALV 25,5%";
  const tileGap = 16;
  const tileW = (USABLE_W - tileGap * 2) / 3;
  const tileH = 104;

  const colGap = 32;
  const leftColW = Math.round(USABLE_W * 0.42);
  const rightColW = USABLE_W - leftColW - colGap;
  const yearChartH = 230;

  const titleH = 36;
  const tilesH = tileH + 24;
  const yearTableH = 62; // keep in sync with drawYearTable's own returned height
  const rightColH = 28 + yearTableH + 16 + yearChartH;
  // The right column (table + chart) sets the shared height; the category
  // list on the left is stretched to fill it (drawCategoryList), so both
  // columns come out the same height instead of the shorter one leaving
  // a gap above the next section.
  const columnsH = rightColH;
  const actionBoxH = 24 + 90; // mirrors drawActionBox's own math

  const totalH = PAD + titleH + tilesH + columnsH + 24 + actionBoxH + PAD;

  const canvas = document.createElement("canvas");
  canvas.width = IMG_WIDTH;
  canvas.height = totalH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let cursorY = PAD;

  // Block title
  ctx.textAlign = "left";
  ctx.fillStyle = GREEN;
  ctx.font = "bold 16px Lato, sans-serif";
  ctx.fillText("CAPEX-YHTEENVETO", PAD, cursorY + 14);
  cursorY += titleH;

  // Stat tiles
  drawStatTile(ctx, PAD, cursorY, tileW, tileH, "CAPEX YHTEENSÄ (10 VUOTTA)", fmtEur(capexTotal), `${alvLabel}, ${currentYear} hintataso`);
  drawStatTile(ctx, PAD + tileW + tileGap, cursorY, tileW, tileH, "BRUTTOALAA KOHDEN", fmtM2Month(perGrossM2Month), `${grossArea.toLocaleString("fi-FI")} m² kokonaisala`);
  drawStatTile(ctx, PAD + (tileW + tileGap) * 2, cursorY, tileW, tileH, "NETTOHUONEALAA KOHDEN", fmtM2Month(perNetM2Month), `${netArea.toLocaleString("fi-FI")} m² huoneistoala`);
  cursorY += tilesH;

  // Left column: category breakdown as progress bars, stretched to the
  // same height as the right column (columnsH === rightColH).
  drawCategoryList(ctx, PAD, cursorY, leftColW, categories, columnsH);

  // Right column: year table + per-year chart.
  const rightX = PAD + leftColW + colGap;
  let rightY = cursorY;
  rightY += drawSectionTitle(ctx, rightX, rightY, "Kustannusarvio vuosittain, 1000 €");
  rightY += drawYearTable(ctx, rightX, rightY, rightColW, years, footerTotals, capexTotal) + 16;
  const yearChartTotals = footerTotals.map(v => v || 0);
  const yearCanvas = renderYearChart(years, yearChartTotals, rightColW, yearChartH);
  // Explicit destination width/height (not just x, y) so this always
  // draws at exactly rightColW × yearChartH regardless of the source
  // canvas's actual pixel size — see the devicePixelRatio note above.
  ctx.drawImage(yearCanvas, rightX, rightY, rightColW, yearChartH);

  cursorY += columnsH + 24;

  // Action-proposals summary
  drawActionBox(ctx, PAD, cursorY, USABLE_W, periods, capexTotal, alvLabel);

  return canvas.toDataURL("image/png");
}

// Shared by both the PDF block (below) and CapexYhteenveto.jsx's own
// "preview PDF image" button — lets the exact image that would be
// embedded in the PDF be opened and checked without running the full PDF
// generation pipeline. Returns null if the PTS data isn't loaded yet.
export function buildCapexYhteenvetoImage({ ptsContext, rakennusData, mergedRakennusData, hide11thYear = false, alviton = false }) {
  if (!ptsContext) return null;
  const { years: allYears, currentYear, footerTotals: allFooterTotals, tekniikkaYhteensa, lviYhteensa, sahkoYhteensa, tutkimusYhteensa } = ptsContext;
  if (!allFooterTotals?.length) return null;

  // Display-only truncation — slice the per-year arrays at the point each
  // is summed/drawn; the underlying PTS data itself is untouched.
  const years = hide11thYear ? allYears.slice(0, 10) : allYears;
  const footerTotals = allFooterTotals.slice(0, years.length);
  const sliceVals = (arr) => (arr || []).slice(0, years.length);

  const grossArea = getArea(rakennusData, mergedRakennusData, "kokonaisala");
  const netArea = getArea(rakennusData, mergedRakennusData, "huoneistoala");

  const capexTotal = sumThousands(footerTotals);
  const avgAnnual = capexTotal / years.length;
  const perGrossM2Month = avgAnnual / grossArea / 12;
  const perNetM2Month = avgAnnual / netArea / 12;

  // "6–10 v" label stays fixed even with hide11thYear on — footerTotals is
  // already truncated to `years.length` above, and .slice() clamps to the
  // array's actual length on its own, so bucketSum(6, 10) safely sums
  // whatever's left (6–9) without needing a second, label-driven truncation.
  const bucketSum = (start, end) => sumThousands(footerTotals.slice(start, end + 1));
  const periods = [
    { label: "0–1 v", total: bucketSum(0, 0) },
    { label: "2–5 v", total: bucketSum(1, 5) },
    { label: "6–10 v", total: bucketSum(6, 10) },
  ];

  const categorySource = { tekniikkaYhteensa: sliceVals(tekniikkaYhteensa), lviYhteensa: sliceVals(lviYhteensa), sahkoYhteensa: sliceVals(sahkoYhteensa), tutkimusYhteensa: sliceVals(tutkimusYhteensa) };
  const categories = CATEGORIES.map(c => ({ ...c, total: sumThousands(categorySource[c.key]) }));

  return buildCapexImage({
    capexTotal, currentYear, perGrossM2Month, grossArea, perNetM2Month, netArea,
    categories, years, footerTotals, periods, alviton,
  });
}

export async function renderCapexYhteenveto({ ptsContext, content, rakennusData, mergedRakennusData, contentWidth = CONTENT_WIDTH, hide11thYear = false, alviton = false }) {
  const imageBase64 = buildCapexYhteenvetoImage({ ptsContext, rakennusData, mergedRakennusData, hide11thYear, alviton });
  if (!imageBase64) return;

  content.push({ image: imageBase64, width: contentWidth, margin: [0, 0, 0, bottomMarginItemGlobal] });
}
